import { initializeApp } from 'firebase/app';
import { logError, logInfo } from 'utils/logger';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { saveFirebaseToken } from 'api/api';
import { getAnalytics } from 'firebase/analytics';
import { isBot } from 'utils/botDetection';

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: 'AIzaSyBsSWkk074ULdv9Pg7uT8WWyJ26Q2ajulg',
  authDomain: 'downkount-app.firebaseapp.com',
  projectId: 'downkount-app',
  storageBucket: 'downkount-app.appspot.com',
  messagingSenderId: '1007421795916',
  appId: '1:1007421795916:web:ad931701a7775724254ee9',
  measurementId: 'G-FM5Y3GECX1',
};

// Lazy initialization - only initialize when needed
let firebaseApp = null;
let messaging = null;
let analytics = null;
let initializationAttempted = false;
let initializationFailed = false;

const isFirebaseSupported = () => {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof indexedDB !== 'undefined' &&
      'serviceWorker' in navigator
    );
  } catch (error) {
    return false;
  }
};

const isMessagingSupported = () => {
  try {
    // Firebase Cloud Messaging only works reliably on Chrome/Chromium browsers
    const userAgent = navigator.userAgent.toLowerCase();
    const isChrome =
      /chrome|chromium|crios/.test(userAgent) && !/edg/.test(userAgent);
    const isDesktop =
      !/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent
      );

    // Only enable messaging on Chrome desktop
    return (
      isChrome &&
      isDesktop &&
      'serviceWorker' in navigator &&
      'PushManager' in window
    );
  } catch (error) {
    return false;
  }
};

const initializeFirebase = () => {
  if (initializationAttempted) {
    return {
      firebaseApp,
      messaging,
      analytics,
      success: !initializationFailed,
    };
  }

  initializationAttempted = true;

  if (!isFirebaseSupported()) {
    logInfo('Firebase not supported in this browser environment');
    initializationFailed = true;
    return {
      firebaseApp: null,
      messaging: null,
      analytics: null,
      success: false,
    };
  }

  try {
    firebaseApp = initializeApp(firebaseConfig);
    // logInfo('Firebase app initialized successfully');

    // Only initialize messaging on Chrome desktop where it's well supported
    if (isMessagingSupported()) {
      try {
        messaging = getMessaging(firebaseApp);
        // logInfo('Firebase messaging initialized successfully');
      } catch (msgError) {
        // Messaging initialization failed
        console.debug(
          'Firebase messaging initialization failed:',
          msgError.code || msgError.message
        );
        messaging = null;
      }
    } else {
      console.debug(
        'Firebase messaging not supported on this browser - skipping initialization'
      );
      messaging = null;
    }

    // Skip analytics for bots/crawlers to keep stats clean
    if (isBot()) {
      console.debug('Bot detected - skipping analytics initialization');
      analytics = null;
    } else {
      try {
        analytics = getAnalytics(firebaseApp);
        // logInfo('Firebase analytics initialized successfully');
      } catch (analyticsError) {
        logError(
          'Firebase analytics initialization failed (non-critical)',
          analyticsError
        );
        analytics = null; // Explicitly set to null on error
      }
    }

    return { firebaseApp, messaging, analytics, success: true };
  } catch (error) {
    logError('Firebase initialization failed', error);
    initializationFailed = true;
    return {
      firebaseApp: null,
      messaging: null,
      analytics: null,
      success: false,
    };
  }
};

// Register service worker only if messaging is supported (Chrome desktop)
if (isMessagingSupported()) {
  navigator.serviceWorker
    .register('firebase-messaging-sw.js')
    .then(function (registration) {
      logInfo(
        'Service worker registration successful, scope:',
        registration.scope
      );
    })
    .catch(function (err) {
      logError('Service worker registration failed', err);
    });
} else {
  console.debug(
    'Service worker registration skipped - messaging not supported on this browser'
  );
}

export const getFirebaseInstances = () => {
  const result = initializeFirebase();
  return result;
};

export const getAnalyticsInstance = () => {
  const { analytics } = initializeFirebase();
  return analytics;
};

export const safeLogEvent = (eventName, eventParams = {}) => {
  const analytics = getAnalyticsInstance();
  if (analytics && analytics.app) {
    try {
      logEvent(analytics, eventName, eventParams);
    } catch (error) {
      console.debug('Analytics logging failed:', error);
    }
  } else {
    console.debug('Analytics not available, skipping event:', eventName);
  }
};

export const getMessagingInstance = () => {
  const { messaging } = initializeFirebase();
  return messaging;
};

export const fetchToken = async ({ userId, siteUrl }) => {
  const messagingInstance = getMessagingInstance();

  if (!messagingInstance) {
    logInfo('Firebase messaging not available, skipping token fetch');
    return Promise.resolve();
  }

  try {
    // Ensure push subscription exists before getting FCM token
    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      const vapidKey =
        'BFsOM7wMLRY8IHWoepHvYlP0Sv_-co68vRilt48CKQITDQQHkiHb960LCyoXUnmh-Hat2muDjoRQhr0x7N2CEuM';
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
    }

    const currentToken = await getToken(messagingInstance, {
      vapidKey:
        'BFsOM7wMLRY8IHWoepHvYlP0Sv_-co68vRilt48CKQITDQQHkiHb960LCyoXUnmh-Hat2muDjoRQhr0x7N2CEuM',
    });

    if (currentToken && userId) {
      saveFirebaseToken({
        siteUrl: `${siteUrl}`,
        firebaseToken: `${currentToken}`,
        userId: userId,
      });
    }
  } catch (err) {
    logError('Failed to retrieve Firebase messaging token', err);
  }
};

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const onMessageListener = () => {
  const messagingInstance = getMessagingInstance();

  if (!messagingInstance) {
    // Return a promise that never resolves instead of rejecting
    // This prevents uncaught promise rejection errors in unsupported browsers
    return new Promise(() => {
      console.debug(
        'Firebase messaging not available - listener not registered'
      );
    });
  }

  return new Promise((resolve) => {
    onMessage(messagingInstance, (payload) => {
      resolve(payload);
    });
  });
};
